import { ref, computed } from 'vue';
import { signIn, signUp, confirmSignUp, signOut, getCurrentUser as amplifyGetCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword, resendSignUpCode } from 'aws-amplify/auth';

// Global auth state
const user = ref(null);
const isLoading = ref(false);
const error = ref('');

export function useAuth() {
  const isAuthenticated = computed(() => !!user.value);

  // Get current authenticated user
  const getCurrentUser = async () => {
    try {
      isLoading.value = true;
      // CRITICAL: Restore session tokens before checking user
      await fetchAuthSession();
      const currentUser = await amplifyGetCurrentUser();
      user.value = currentUser;
      return currentUser;
    } catch (err) {
      user.value = null;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  // Sign in user
  const signInUser = async (email, password) => {
    try {
      isLoading.value = true;
      error.value = '';
      const result = await signIn({ username: email, password });
      
      // Only set user if fully signed in
      if (result.isSignedIn) {
        user.value = result;
      }
      // Don't set user.value = null here - let the state machine handle it
      
      return result;
    } catch (err) {
      error.value = err.message || 'Sign in failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Sign up user
  const signUpUser = async (email, password, attributes = {}) => {
    try {
      isLoading.value = true;
      error.value = '';
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...attributes
          }
        }
      });
      return result;
    } catch (err) {
      error.value = err.message || 'Sign up failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Confirm sign up with verification code
  const confirmSignUpUser = async (email, code) => {
    try {
      isLoading.value = true;
      error.value = '';
      const result = await confirmSignUp({ username: email, confirmationCode: code });
      return result;
    } catch (err) {
      error.value = err.message || 'Confirmation failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Sign out user
  const signOutUser = async () => {
    try {
      isLoading.value = true;
      await signOut();
      user.value = null;
    } catch (err) {
      error.value = err.message || 'Sign out failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Get JWT tokens for API calls (both access and ID tokens)
  const getTokens = async () => {
    try {
      const session = await fetchAuthSession();
      return {
        accessToken: session.tokens?.accessToken?.toString(),
        idToken: session.tokens?.idToken?.toString()
      };
    } catch (err) {
      throw new Error('Failed to get tokens');
    }
  };

  // Get JWT access token for API calls
  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (err) {
      throw new Error('Failed to get access token');
    }
  };

  // Get JWT ID token for API calls (typically used with API Gateway)
  const getIdToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (err) {
      throw new Error('Failed to get ID token');
    }
  };

  // Reset password
  const forgotPassword = async (email) => {
    try {
      isLoading.value = true;
      error.value = '';
      const result = await resetPassword({ username: email });
      return result;
    } catch (err) {
      error.value = err.message || 'Password reset failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Confirm password reset
  const forgotPasswordSubmit = async (email, code, newPassword) => {
    try {
      isLoading.value = true;
      error.value = '';
      const result = await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return result;
    } catch (err) {
      error.value = err.message || 'Password reset confirmation failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Clear error
  const clearError = () => {
    error.value = '';
  };

  // Resend confirmation code
  const resendConfirmationCode = async (email) => {
    try {
      isLoading.value = true;
      error.value = '';
      const result = await resendSignUpCode({ username: email });
      return result;
    } catch (err) {
      error.value = err.message || 'Failed to resend confirmation code';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    user: computed(() => user.value),
    isAuthenticated,
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    getCurrentUser,
    signIn: signInUser,
    signUp: signUpUser,
    confirmSignUp: confirmSignUpUser,
    signOut: signOutUser,
    getTokens,
    getAccessToken,
    getIdToken,
    forgotPassword,
    forgotPasswordSubmit,
    resendConfirmationCode,
    clearError
  };
}